import { useId, useUncontrolled } from '@mantine/hooks';
import {
  Combobox,
  factory,
  Factory,
  getOptionsLockup,
  getParsedComboboxData,
  OptionsData,
  OptionsDropdown,
  Pill,
  PillsInput,
  useCombobox,
} from '@mantine/core';
import classNames from '@/components/StyledInputFields/Inputs.module.css';
import React, { ReactNode } from 'react';
import CustomSelectOption from '@/components/CustomSelectOption.tsx';

export interface SimplifiedMultiSelectProps {
  data: OptionsData;
  value?: string[];
  onChange?: (value: string[]) => void;
  label: string;
  placeholder: string;
  pills: ReactNode[];
  error?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  required?: boolean;
}

export type SimplifiedMultiSelectFactory = Factory<{
  props: SimplifiedMultiSelectProps;
  ref: HTMLInputElement;
}>;

export const MultiSelectWithCustomPills = factory<SimplifiedMultiSelectFactory>((props) => {
  const {
    data,
    value,
    onChange,
    label,
    placeholder,
    pills,
    error,
    description,
    disabled,
    required,
    ref,
  } = props;

  const id = useId();
  const parsedData = getParsedComboboxData(data);
  const optionsLockup = getOptionsLockup(parsedData);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [_value, setValue] = useUncontrolled<string[]>({
    value,
    defaultValue: [],
    finalValue: [],
    onChange,
  });

  const [_searchValue, setSearchValue] = useUncontrolled({
    value: undefined,
    defaultValue: '',
    finalValue: '',
  });

  const handleSearchChange = (val: string) => {
    setSearchValue(val);
    combobox.resetSelectedOption();
  };

  const handleOptionSelect = (val: string) => {
    const optionValue = optionsLockup[val].value;
    if (typeof optionValue !== 'string') {
      return;
    } else if (_value.includes(optionValue)) {
      setValue(_value.filter((v) => v !== optionValue));
    } else {
      setValue([..._value, optionValue]);
    }
    handleSearchChange('');
    combobox.updateSelectedOptionIndex('selected');
  };

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSelect}
      classNames={{ option: classNames.option }}
    >
      <Combobox.DropdownTarget>
        <PillsInput
          label={label}
          onClick={() => combobox.openDropdown()}
          id={id}
          ref={ref}
          data-expanded={combobox.dropdownOpened || undefined}
          error={error}
          styles={{ label: { marginBottom: '7px' } }}
          description={description}
          disabled={disabled}
          required={required}
        >
          <Pill.Group disabled={disabled}>
            {pills}
            <Combobox.EventsTarget>
              <PillsInput.Field
                ref={ref}
                id={id}
                placeholder={placeholder}
                value={_searchValue}
                onChange={(e) => {
                  handleSearchChange(e.currentTarget.value);
                  combobox.openDropdown();
                }}
                onFocus={() => combobox.openDropdown()}
                onBlur={() => {
                  combobox.closeDropdown();
                  handleSearchChange('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && _searchValue.length === 0 && _value.length > 0) {
                    setValue(_value.slice(0, -1));
                  }
                }}
                className={classNames?.input}
                disabled={disabled}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <OptionsDropdown
        data={parsedData}
        hidden={disabled}
        value={_value}
        search={_searchValue}
        filter={undefined}
        filterOptions={true}
        limit={undefined}
        withScrollArea={undefined}
        maxDropdownHeight={undefined}
        // We'll render our own checkmark to match the single-select UI:
        withCheckIcon={false}
        nothingFoundMessage="No options found"
        hiddenWhenEmpty={false}
        unstyled={undefined}
        labelId={undefined}
        aria-label={undefined}
        scrollAreaProps={undefined}
        renderOption={({ option, checked }) => {
          const o = option as any;
          return (
            <CustomSelectOption label={o.label} leftSection={o.leftSection} checked={checked} />
          );
        }}
      />
    </Combobox>
  );
});
